import { Card, Row, Col } from 'antd';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';

/**
 * OrderHistoryCard - Displays chronological timeline of order status and payment updates
 */
const OrderHistoryCard = ({ dedupedHistory, finalAmount }) => {
    if (!dedupedHistory || dedupedHistory.length === 0) return null;

    return (
        <Card
            className="mb-3"
            style={{ position: 'sticky', top: '20px' }}
            headStyle={{
                background: '#1890ff',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                padding: '16px 24px',
            }}
            bodyStyle={{ padding: '24px', background: '#f5f7fa' }}
            bordered={false}
            title={<span style={{ color: '#fff', fontSize: '18px', fontWeight: '600' }}>Order History</span>}
        >
            {(() => {
                const groupedHistory = [];
                dedupedHistory.forEach((item) => {
                    const last = groupedHistory[groupedHistory.length - 1];
                    const isDuplicate = last &&
                        last.orderStatus === item.orderStatus &&
                        last.paymentStatus === item.paymentStatus &&
                        last.paidAmount === item.paidAmount &&
                        last.paymentMode === item.paymentMode &&
                        last.additionalInfo?.reason === item.additionalInfo?.reason &&
                        last.createdByName === item.createdByName;

                    if (isDuplicate) {
                        last.count = (last.count || 1) + 1;
                        last.latestDate = item.createdAt;
                    } else {
                        groupedHistory.push({ ...item, count: 1, latestDate: item.createdAt });
                    }
                });

                return groupedHistory.map((history, index) => {
                    const paidAmount = history.paidAmount || 0;
                    const totalAmount = history.amount || 0;
                    const updatedPaidAmount = history.updatedPaidAmount || 0;
                    const isNonPaymentEntry = ['Initialize', 'Failed'].includes(history.orderStatus) || updatedPaidAmount === 0;
                    const hPercent = (!isNonPaymentEntry && finalAmount > 0)
                        ? Math.min(100, Math.round((updatedPaidAmount / finalAmount) * 100))
                        : 0;
                    const paymentPercent = hPercent;

                    return (
                        <div
                            key={history._id || index}
                            className="mb-3 p-3"
                            style={{
                                background: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                transition: 'all 0.3s ease',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            {history.count > 1 && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        background: '#f0f5ff',
                                        color: '#1890ff',
                                        padding: '4px 12px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        borderBottomLeftRadius: '12px',
                                        border: '1px solid #adc6ff',
                                        borderTop: 'none',
                                        borderRight: 'none',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Repeated {history.count} Times
                                </div>
                            )}

                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                    <div style={{ fontSize: '11px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                        Entry #{index + 1}
                                    </div>
                                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#262626', marginTop: '4px' }}>
                                        {history.orderStatus}
                                    </div>
                                    {history.paymentStatus && (
                                        <div
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                color: history.paymentStatus === 'Completed' ? '#52c41a' : history.paymentStatus === 'Pending' ? '#faad14' : '#8c8c8c',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                marginTop: '4px'
                                            }}
                                        >
                                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />
                                            {history.paymentStatus}
                                        </div>
                                    )}
                                </div>
                                <div className="text-end">
                                    {paidAmount > 0 && <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Paid Amount</div>}
                                    <div style={{ fontSize: paidAmount > 0 ? '20px' : '17px', fontWeight: '800', color: '#1890ff' }}>
                                        ₹{paidAmount > 0 ? paidAmount.toLocaleString() : totalAmount.toLocaleString()}
                                    </div>
                                    {!isNonPaymentEntry && updatedPaidAmount > 0 && (
                                        <div style={{ fontSize: '11px', color: '#52c41a', marginTop: '4px', background: '#f6ffed', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                                            Total Paid: ₹{updatedPaidAmount.toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Payment Progress Bar */}
                            {!isNonPaymentEntry && paymentPercent > 0 && (
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between mb-1">
                                        <span style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: '500' }}>Payment Collection Progress</span>
                                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#1890ff' }}>{paymentPercent}%</span>
                                    </div>
                                    <div style={{ width: '100%', height: '8px', background: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                                        <div
                                            style={{
                                                width: `${paymentPercent}%`,
                                                height: '100%',
                                                background: paymentPercent === 100 ? 'linear-gradient(90deg, #52c41a, #73d13d)' : 'linear-gradient(90deg, #1890ff, #40a9ff)',
                                                transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Member Days Deducted */}
                            {history.additionalInfo?.memberDaysDeducted > 0 && (
                                <div className="mb-3" style={{ background: '#f0fdf4', padding: '10px 14px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center gap-2">
                                            <FeatherIcon icon="user-check" size={14} style={{ color: '#166534' }} />
                                            <span style={{ fontSize: '12px', color: '#166534', fontWeight: '600' }}>Membership Days Benefit</span>
                                        </div>
                                        <span style={{ fontSize: '14px', fontWeight: '800', color: '#15803d' }}>
                                            {history.additionalInfo.memberDaysDeducted} Days
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Amount Details Summary */}
                            <div className="mb-3 p-3" style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                <Row gutter={[16, 8]}>
                                    <Col xs={12}>
                                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase' }}>Final Payable</div>
                                        <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                                            ₹{finalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </div>
                                    </Col>
                                    {!isNonPaymentEntry && updatedPaidAmount > 0 && (
                                        <Col xs={12}>
                                            <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase' }}>Total Collected</div>
                                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#10b981' }}>
                                                ₹{updatedPaidAmount.toLocaleString()}
                                            </div>
                                        </Col>
                                    )}
                                    {!isNonPaymentEntry && updatedPaidAmount > 0 && finalAmount - updatedPaidAmount > 0 && (
                                        <Col xs={24} className="mt-1">
                                            <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span style={{ fontSize: '11px', color: '#64748b' }}>Balance Outstanding</span>
                                                <span style={{ fontSize: '14px', fontWeight: '700', color: '#f59e0b' }}>
                                                    ₹{(finalAmount - updatedPaidAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </div>

                            <Row gutter={[16, 12]} className="mt-2">
                                {history.paymentMode && (
                                    <Col xs={12} sm={8}>
                                        <div style={{ fontSize: '10px', color: '#8c8c8c', textTransform: 'uppercase' }}>Method</div>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#434343', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <FeatherIcon icon="credit-card" size={12} />
                                            {history.paymentMode}
                                        </div>
                                    </Col>
                                )}
                                {(history.additionalInfo?.createdByName || history.createdByName) && (
                                    <Col xs={12} sm={8}>
                                        <div style={{ fontSize: '10px', color: '#8c8c8c', textTransform: 'uppercase' }}>Created By</div>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#434343' }}>
                                            {history.additionalInfo?.createdByName || history.createdByName}
                                        </div>
                                    </Col>
                                )}
                                {(history.updatedByName) && (
                                    <Col xs={12} sm={8}>
                                        <div style={{ fontSize: '10px', color: '#8c8c8c', textTransform: 'uppercase' }}>Collected By</div>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#434343' }}>
                                            {history.updatedByName}
                                        </div>
                                    </Col>
                                )}
                                {history.createdAt && (
                                    <Col xs={24} sm={12}>
                                        <div style={{ fontSize: '10px', color: '#8c8c8c', textTransform: 'uppercase' }}>Timeline</div>
                                        <div style={{ fontSize: '12px', fontWeight: '500', color: '#595959' }}>
                                            {new Date(history.createdAt).toLocaleString('en-IN', {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                            {history.count > 1 && (
                                                <span style={{ marginLeft: '8px', color: '#8c8c8c', fontSize: '11px' }}>
                                                    (First entry)
                                                </span>
                                            )}
                                        </div>
                                    </Col>
                                )}
                                {history.additionalInfo?.reason && (
                                    <Col xs={24}>
                                        {history.additionalInfo.reason === 'Room Upgrade' ? (
                                            <div style={{ marginTop: '4px', padding: '12px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: '8px' }}>
                                                <div className="d-flex align-items-center mb-2">
                                                    <FeatherIcon icon="arrow-up-circle" size={16} className="me-2" style={{ color: '#fa8c16' }} />
                                                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#fa8c16' }}>{history.additionalInfo.reason}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
                                                    <div>
                                                        <div style={{ fontSize: '9px', color: '#8c8c8c', textTransform: 'uppercase' }}>Old Base</div>
                                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#595959' }}>₹{history.additionalInfo.previousAmount?.toLocaleString()}</div>
                                                    </div>
                                                    <FeatherIcon icon="plus" size={12} style={{ color: '#52c41a' }} />
                                                    <div style={{ padding: '4px 10px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
                                                        <div style={{ fontSize: '9px', color: '#8c8c8c', textTransform: 'uppercase' }}>Upgrade Fee</div>
                                                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#52c41a' }}>+₹{history.additionalInfo.priceDelta?.toLocaleString()}</div>
                                                    </div>
                                                    <div style={{ fontSize: '14px', color: '#bfbfbf' }}>=</div>
                                                    <div>
                                                        <div style={{ fontSize: '9px', color: '#8c8c8c', textTransform: 'uppercase' }}>Revised Total</div>
                                                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#1890ff' }}>₹{history.amount?.toLocaleString()}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ fontSize: '12px', color: '#1890ff', marginTop: '4px', padding: '10px 14px', background: '#e6f7ff', borderRadius: '6px', border: '1px solid #91d5ff', display: 'flex', alignItems: 'center' }}>
                                                <FeatherIcon icon="info" size={14} className="me-2" />
                                                <span style={{ fontWeight: '500' }}>{history.additionalInfo.reason}</span>
                                            </div>
                                        )}
                                    </Col>
                                )}
                            </Row>
                        </div>
                    );
                });
            })()}
        </Card>
    );
};

export default OrderHistoryCard;
